import React, {
  lazy, useEffect, useMemo, useRef,
} from 'react'
import { List, Map as ImMap } from 'immutable'
import { useSelector } from 'react-redux'
import { useFeatureFlagVariantKey } from '@posthog/react'
import posthog from 'posthog-js'
import Sherpa, { TYPE_LARGE } from '../Sherpa/index'
import { POSTHOG_FEATURE_FLAGS } from '../PostHog/index'
import { get as getConfig } from '../../config/index'
import { parse as parseQuery } from '../../services/query-string/index'
import { hasContentHighlightsAccess } from '../../services/content-highlights/access'
import getSearchPageExperimentExposure from '../../services/content-highlights/search-page-experiment-exposure'

const LoadableSearch = lazy(
  () => import('../SearchPage/index'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)

function readSearchQuery (location) {
  if (location?.search) {
    const { q: qParam } = parseQuery(location.search)
    return String(qParam || '').trim()
  }
  return String(location?.query?.q || '').trim()
}

export default function SearchPageSwitch (props) {
  const { location } = props
  const auth = useSelector((state) => state.auth)
  const bootstrapComplete = useSelector((state) => !!state.app?.get?.('bootstrapComplete'))
  const roleIds = ImMap.isMap(auth) ? auth.get('roleIds', List()) : List()
  const rawSubscriptions = ImMap.isMap(auth) ? auth.get('subscriptions') : null
  const subscriptions = List.isList(rawSubscriptions) ? rawSubscriptions : List()
  const subscriptionsHydrated = rawSubscriptions != null
  const hasJwt = ImMap.isMap(auth) ? !!auth.get('jwt') : false
  const processingRenew = ImMap.isMap(auth) ? !!auth.get('processingRenew') : false
  const hasHighlightsAccess = hasContentHighlightsAccess({ roleIds, subscriptions })

  const q = useMemo(() => readSearchQuery(location), [location])
  const unifiedSearchHelmEnabled = !!getConfig().features.contentHighlights?.unifiedSearch
  const appEnv = getConfig().env

  const aiSearchVariant = useFeatureFlagVariantKey(
    POSTHOG_FEATURE_FLAGS.AI_SEARCH_WEB,
  )
  const unifiedSearchVariant = useFeatureFlagVariantKey(
    POSTHOG_FEATURE_FLAGS.UNIFIED_SEARCH_WEB,
  )

  const exposureFiredRef = useRef(new Set())
  const unifiedSearchVariantKey = typeof unifiedSearchVariant === 'string'
    ? unifiedSearchVariant
    : undefined

  useEffect(() => {
    const exposure = getSearchPageExperimentExposure({
      helmEnabled: unifiedSearchHelmEnabled,
      hasAccess: hasHighlightsAccess,
      query: q,
      unifiedSearchVariant: unifiedSearchVariantKey,
      aiSearchVariant,
      hasJwt,
      processingRenew,
      bootstrapComplete,
      subscriptionsHydrated,
      appEnv,
    })
    if (!exposure) {
      return
    }

    // Unified enrollment can resolve after subscriptions hydrate on auth renew.
    if (exposureFiredRef.current.has(exposure.experiment)) {
      return
    }

    exposureFiredRef.current.add(exposure.experiment)
    posthog.capture('experiment_exposure', exposure.properties)
  }, [
    q,
    unifiedSearchHelmEnabled,
    hasHighlightsAccess,
    unifiedSearchVariantKey,
    aiSearchVariant,
    hasJwt,
    processingRenew,
    bootstrapComplete,
    subscriptionsHydrated,
    appEnv,
  ])

  return <LoadableSearch location={props.location} history={props.history} />
}
